import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Progress } from "@/components/ui/progress";
import { Building2, MapPin, Edit2, Phone, Mail, Globe, Check, ChevronsUpDown, CheckCircle2, Circle, AlertCircle, ShieldCheck, Hash, Briefcase, Calendar } from "lucide-react";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneInputValue } from "@/lib/phone";
import { formatEINForDisplay, formatEINInputValue, stripEINFormatting } from "@/lib/ein";
import { AddressFields } from "@/components/forms/AddressFields";
import { EmployeeDefaultSettingsCard } from "@/components/EmployeeDefaultSettingsCard";
import { composeAddressLine, emptyAddressFields, normalizeAddressFields } from "@/lib/address";
import { formatDate, cn } from "../../../lib/utils";
import { SIC_CODES, getSicCodeLabel, formatSicCode } from "@shared/sicCodes";
import { useProfileCompletion, OVERVIEW_FIELDS, CONTACT_FIELDS } from "@/hooks/useProfileCompletion";
import { useAuth } from "@/hooks/useAuth";
import { ENTITY_TYPES } from "@shared/entityTypes";

export interface ProfileData {
  agencyName?: string;
  agencyLicenseNumber?: string;
  agencyEntityType?: string;
  agencyEIN?: string;
  sicCode?: string;
  establishedDate?: string | Date | null;
  phone?: string;
  contactEmail?: string;
  email?: string;
  website?: string;
  agencyAddress1?: string;
  agencyAddress2?: string;
  agencyCity?: string;
  agencyState?: string;
  agencyZipCode?: string;
  agencyCounty?: string;
  agencyCountry?: string;
  agencyLogo?: string;
}

type OverviewFormValues = {
  agencyName: string;
  agencyLicenseNumber: string;
  agencyEntityType: string;
  agencyEIN: string;
  sicCode: string;
  establishedDate: string;
  specializations: string;
};

type ContactFormValues = {
  phone: string;
  contactEmail: string;
  website: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  county: string;
  country: string;
};

export function ProfileTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const profileCompletion = useProfileCompletion();
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ["/api/user/profile"],
    queryFn: () => fetch("/api/user/profile").then((r) => r.json()),
  });

  const emptyProfile = useMemo(() => ({} as ProfileData), []);
  const profile = data ?? emptyProfile;

  const agencyId = (profile as any)?.agencyId as string | undefined;
  const { data: agencyAdmins = [] } = useQuery({
    queryKey: ["/api/org/admins", "agency", agencyId],
    queryFn: async () => {
      if (!agencyId) return [];
      const r = await fetch(`/api/org/admins?ownerType=agency&ownerId=${agencyId}`, {
        credentials: "include",
      });
      return r.ok ? r.json() : [];
    },
    enabled: !!agencyId,
  });

  const canEdit = useMemo(() => {
    if (!agencyId) return false;
    if (!user?.id) return false;
    return (agencyAdmins as any[]).some((a) => a.id === user.id);
  }, [agencyAdmins, agencyId, user?.id]);

  // Check if a field is complete
  const isFieldComplete = (key: string) => {
    const value = profile[key as keyof ProfileData];
    return value !== null && value !== undefined &&
      (typeof value !== "string" || value.trim() !== "");
  };

  const [editOverviewOpen, setEditOverviewOpen] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [sicOpen, setSicOpen] = useState(false);

  const overviewForm = useForm<OverviewFormValues>({
    defaultValues: {
      agencyName: "",
      agencyLicenseNumber: "",
      agencyEntityType: "",
      agencyEIN: "",
      sicCode: "",
      establishedDate: "",
    },
  });

  const contactForm = useForm<ContactFormValues>({
    defaultValues: {
      phone: "",
      contactEmail: "",
      website: "",
      ...emptyAddressFields(),
    },
  });

  useEffect(() => {
    // Avoid infinite reset loops while profile query is still loading.
    if (!data) return;

    const establishedInputValue = data.establishedDate
      ? new Date(data.establishedDate as any).toISOString().split("T")[0]
      : "";

    overviewForm.reset({
      agencyName: data.agencyName || "",
      agencyLicenseNumber: data.agencyLicenseNumber || "",
      agencyEntityType: data.agencyEntityType || "",
      agencyEIN: formatEINForDisplay(
        data.agencyEIN === null || data.agencyEIN === undefined
          ? ""
          : String(data.agencyEIN)
      ),
      sicCode:
        data.sicCode === null || data.sicCode === undefined
          ? ""
          : String(data.sicCode),
      establishedDate: establishedInputValue,
      specializations: data.specializations || "",
    });

    const addressDefaults = normalizeAddressFields({
      address1: data.agencyAddress1,
      address2: data.agencyAddress2,
      city: data.agencyCity,
      state: data.agencyState,
      zipCode: data.agencyZipCode,
      county: data.agencyCounty,
      country: data.agencyCountry,
    });

    contactForm.reset({
      phone: data.phone || "",
      contactEmail: data.contactEmail || "",
      website: data.website || "",
      ...addressDefaults,
    });
  }, [data]); // intentionally only depends on query data

  const updateUserProfileMutation = useMutation({
    mutationFn: (data: any) => apiRequest("PATCH", `/api/user/profile`, data),
    onSuccess: async (res: Response) => {
      const updated = await res.json();
      queryClient.setQueryData(["/api/user/profile"], updated);
      toast({
        title: "Profile updated",
        description: "Information has been successfully updated.",
        variant: "success",
      });
      setEditOverviewOpen(false);
      setEditContactOpen(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update profile.",
        variant: "destructive",
      });
    },
  });

  const renderContactField = (
    label: string,
    placeholder: string,
    type: string = "text"
  ) => {
    return ({ field }: { field: ControllerRenderProps<ContactFormValues> }) => {
      return (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input {...field} placeholder={placeholder} type={type} />
          </FormControl>
          <FormMessage />
        </FormItem>
      );
    };
  };

  const handleOverviewSubmit = (values: OverviewFormValues) => {
    if (!canEdit) {
      toast({
        title: "Permission denied",
        description: "Only company admins can edit the agency profile.",
        variant: "destructive",
      });
      return;
    }
    updateUserProfileMutation.mutate({
      ...values,
      // Server currently parses agencyEIN with parseInt(), so we must send digits only.
      agencyEIN: values.agencyEIN ? stripEINFormatting(values.agencyEIN) : "",
      // Ensure form uses string values so SIC dropdown re-selects correctly on edit.
      sicCode: values.sicCode ? String(values.sicCode) : "",
    });
  };

  const handleContactSubmit = (values: ContactFormValues) => {
    if (!canEdit) {
      toast({
        title: "Permission denied",
        description: "Only company admins can edit the agency profile.",
        variant: "destructive",
      });
      return;
    }
    updateUserProfileMutation.mutate({
      phone: values.phone,
      contactEmail: values.contactEmail,
      website: values.website,
      agencyAddress1: values.address1,
      agencyAddress2: values.address2,
      agencyCity: values.city,
      agencyState: values.state,
      agencyZipCode: values.zipCode,
      agencyCounty: values.county,
      agencyCountry: values.country,
    });
  };

  const contactEmailDisplay = profile.contactEmail || "";

  const agencyAddressLines = [
    composeAddressLine({
      address1: profile.agencyAddress1,
      address2: profile.agencyAddress2,
      city: profile.agencyCity,
      state: profile.agencyState,
      zipCode: profile.agencyZipCode,
    }),
    profile.agencyCounty,
    profile.agencyCountry,
  ].filter((value): value is string =>
    Boolean(value && value.trim().length > 0)
  );

  const [primaryAddressLine, ...secondaryAddressLines] = agencyAddressLines;

  const primaryAddressElement = primaryAddressLine ? (
    <p className="font-medium">{primaryAddressLine}</p>
  ) : null;

  const secondaryAddressElements = secondaryAddressLines.map((line) => (
    <p key={line} className="text-muted-foreground">
      {line}
    </p>
  ));

  return (
    <div className="space-y-6">
      {/* Profile Completion Card - only show when incomplete and user can edit */}
      {canEdit && !profileCompletion.isLoading && !profileCompletion.isComplete && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50/30">
          <CardContent className="py-4">
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium">Profile Completion</span>
                <span className="text-sm font-semibold text-amber-600">
                  {profileCompletion.completionPercentage}%
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {profileCompletion.completedFields}/{profileCompletion.totalFields} fields
              </span>
            </div>

            {/* Progress bar */}
            <Progress
              value={profileCompletion.completionPercentage}
              className="h-1.5 mb-3 [&>div]:bg-amber-500"
            />

            {/* Two-column layout for sections */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Agency Overview Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">Agency Overview</span>
                  </div>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 px-1.5 text-xs"
                      onClick={() => setEditOverviewOpen(true)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {OVERVIEW_FIELDS.map((field) => {
                    const complete = isFieldComplete(field.key);
                    return (
                      <button
                        key={field.key}
                        onClick={() => setEditOverviewOpen(true)}
                        className={cn(
                          "inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-colors",
                          complete
                            ? "text-green-700 bg-green-100/60 hover:bg-green-100"
                            : "text-gray-500 bg-gray-100/60 hover:bg-gray-200/60"
                        )}
                      >
                        {complete ? (
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                        ) : (
                          <Circle className="h-3 w-3 text-gray-400" />
                        )}
                        <span>
                          {field.label}
                          {field.required && <span className="text-red-500">*</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contact Information Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">Contact Information</span>
                  </div>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 px-1.5 text-xs"
                      onClick={() => setEditContactOpen(true)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {CONTACT_FIELDS.map((field) => {
                    const complete = isFieldComplete(field.key);
                    return (
                      <button
                        key={field.key}
                        onClick={() => setEditContactOpen(true)}
                        className={cn(
                          "inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-colors",
                          complete
                            ? "text-green-700 bg-green-100/60 hover:bg-green-100"
                            : "text-gray-500 bg-gray-100/60 hover:bg-gray-200/60"
                        )}
                      >
                        {complete ? (
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                        ) : (
                          <Circle className="h-3 w-3 text-gray-400" />
                        )}
                        <span>{field.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>Agency Overview</span>
            </CardTitle>
            {canEdit && (
              <Dialog open={editOverviewOpen} onOpenChange={setEditOverviewOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Edit Agency Overview</DialogTitle>
                </DialogHeader>
                <div
                  onSubmit={overviewForm.handleSubmit(handleOverviewSubmit)}
                  className="space-y-4"
                >
                  <div>
                    <Label htmlFor="agencyName">Agency Name</Label>
                    <Input
                      id="agencyName"
                      {...overviewForm.register("agencyName", {
                        required: true,
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="agencyLicenseNumber">License Number</Label>
                    <Input
                      id="agencyLicenseNumber"
                      {...overviewForm.register("agencyLicenseNumber", {
                        required: true,
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="agencyEntityType">Entity Type</Label>
                    <Controller
                      control={overviewForm.control}
                      name="agencyEntityType"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select entity type" />
                          </SelectTrigger>
                          <SelectContent>
                            {ENTITY_TYPES.map((et) => (
                              <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div>
                    <Label htmlFor="agencyEIN">EIN</Label>
                    <Controller
                      control={overviewForm.control}
                      name="agencyEIN"
                      rules={{ required: true }}
                      render={({ field }) => {
                        const handleEINChange = (
                          event: ChangeEvent<HTMLInputElement>
                        ) => {
                          const formattedValue = formatEINInputValue(
                            event.target.value
                          );
                          field.onChange(formattedValue);
                        };

                        return (
                          <Input
                            id="agencyEIN"
                            {...field}
                            value={field.value || ""}
                            placeholder="XX-XXXXXXX"
                            onChange={handleEINChange}
                          />
                        );
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label htmlFor="sicCode">SIC Code</Label>
                    <Controller
                      control={overviewForm.control}
                      name="sicCode"
                      rules={{ required: true }}
                      render={({ field }) => {
                        const selectedSic = SIC_CODES.find(
                          (opt) => String(opt.code) === String(field.value ?? "")
                        );
                        const fullLabel = selectedSic
                          ? `${formatSicCode(selectedSic.code)} - ${selectedSic.industryTitle}`
                          : "Select SIC code";
                        const maxLength = 40;
                        const label =
                          fullLabel.length > maxLength
                            ? fullLabel.slice(0, maxLength) + "..."
                            : fullLabel;
                        return (
                          <Popover open={sicOpen} onOpenChange={setSicOpen}>
                            <PopoverTrigger title={fullLabel} asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={sicOpen}
                                className="w-full justify-between text-left font-normal h-10 min-w-0"
                              >
                                <span className="truncate block min-w-0 flex-1">
                                  {label}
                                </span>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 w-[400px]"
                              align="start"
                              sideOffset={4}
                              onOpenAutoFocus={(e) => e.preventDefault()}
                              onWheel={(e) => e.stopPropagation()}
                            >
                              <Command shouldFilter={true}>
                                <CommandInput placeholder="Search code or industry" />
                                <CommandEmpty>No results found.</CommandEmpty>
                                <CommandList style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                  <CommandGroup>
                                    {SIC_CODES.map((opt) => (
                                      <CommandItem
                                        key={opt.code}
                                        value={`${formatSicCode(opt.code)} ${opt.industryTitle}`}
                                        onSelect={() => {
                                          field.onChange(String(opt.code));
                                          setSicOpen(false);
                                        }}
                                        className="cursor-pointer"
                                      >
                                        <div className="flex items-center w-full min-w-0">
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4 shrink-0",
                                              String(opt.code) ===
                                                String(field.value ?? "")
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                          <span className="truncate">
                                            {formatSicCode(opt.code)} - {opt.industryTitle}
                                          </span>
                                        </div>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        );
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="establishedDate">Established</Label>
                    <Input
                      id="establishedDate"
                      type="date"
                      {...overviewForm.register("establishedDate", {
                        required: true,
                      })}
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditOverviewOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      onClick={overviewForm.handleSubmit(handleOverviewSubmit)}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium">{profile.agencyName || "-"}</p>
              <p className="text-sm text-muted-foreground">Agency Name</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium font-mono">{profile.agencyLicenseNumber || "-"}</p>
              <p className="text-sm text-muted-foreground">License Number</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium">{profile.agencyEntityType || "-"}</p>
              <p className="text-sm text-muted-foreground">Entity Type</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Hash className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium font-mono">
                {formatEINForDisplay(
                  profile.agencyEIN === null || profile.agencyEIN === undefined
                    ? ""
                    : String(profile.agencyEIN)
                ) || "-"}
              </p>
              <p className="text-sm text-muted-foreground">EIN</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium">
                {profile.sicCode ? getSicCodeLabel(Number(profile.sicCode)) : "-"}
              </p>
              <p className="text-sm text-muted-foreground">SIC Code</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium">{formatDate(profile.establishedDate as any) || "-"}</p>
              <p className="text-sm text-muted-foreground">Established</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="h-5 w-5" />
              <span>Contact Information</span>
            </CardTitle>
            {canEdit && (
              <Dialog open={editContactOpen} onOpenChange={setEditContactOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Edit Contact Information</DialogTitle>
                  </DialogHeader>
                  <Form {...contactForm}>
                    <div
                      onSubmit={contactForm.handleSubmit(handleContactSubmit)}
                      className="space-y-4"
                    >
                      <Label htmlFor="phone">Phone</Label>
                      <Controller
                        control={contactForm.control}
                        name="phone"
                        rules={{ required: true }}
                        render={({ field }) => {
                          const handlePhoneChange = (
                            event: ChangeEvent<HTMLInputElement>
                          ) => {
                            const formattedValue = formatPhoneInputValue(
                              event.target.value
                            );

                            field.onChange(formattedValue);
                          };

                          return (
                            <Input
                              id="phone"
                              {...field}
                              value={field.value || ""}
                              placeholder="(555) 123-4567"
                              onChange={handlePhoneChange}
                            />
                          );
                        }}
                      />
                      <FormField
                        control={contactForm.control}
                        name="contactEmail"
                        render={renderContactField(
                          "Email",
                          "name@example.com",
                          "email"
                        )}
                      />
                      <FormField
                        control={contactForm.control}
                        name="website"
                        render={renderContactField("Website", "www.example.com")}
                      />

                      <AddressFields control={contactForm.control} />

                      <div className="flex justify-end space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setEditContactOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          onClick={contactForm.handleSubmit(handleContactSubmit)}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  </Form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium">{profile.phone}</p>
              <p className="text-sm text-muted-foreground">Main Office</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium">{contactEmailDisplay}</p>
              <p className="text-sm text-muted-foreground">General Inquiries</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium">{profile.website}</p>
              <p className="text-sm text-muted-foreground">Website</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
            <div>
              {primaryAddressElement}
              {secondaryAddressElements}
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="md:col-span-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>Agency Branding</span>
            </CardTitle>
          </div>
          <CardDescription>
            Your agency logo will appear on public quote pages alongside the Administr logo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {profile.agencyLogo ? (
                <div className="relative group">
                  <img
                    src={profile.agencyLogo}
                    alt="Agency Logo"
                    className="h-24 w-auto max-w-[200px] object-contain rounded-lg border bg-white p-2"
                  />
                  {canEdit && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => {
                        updateUserProfileMutation.mutate({ agencyLogo: null });
                      }}
                    >
                      ×
                    </Button>
                  )}
                </div>
              ) : canEdit ? (
                <label
                  htmlFor="logoUpload"
                  className="h-24 w-40 rounded-lg border-2 border-dashed border-muted-foreground/25 flex flex-col items-center justify-center bg-muted/50 cursor-pointer hover:border-primary hover:bg-muted transition-colors"
                >
                  <Building2 className="h-8 w-8 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">Click to upload</span>
                </label>
              ) : (
                <div className="h-24 w-40 rounded-lg border-2 border-dashed border-muted-foreground/25 flex flex-col items-center justify-center bg-muted/50">
                  <Building2 className="h-8 w-8 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">No logo set</span>
                </div>
              )}
            </div>
            {canEdit && (
              <div className="flex-1 space-y-4">
                <div>
                  <Label htmlFor="logoUpload">Upload Logo</Label>
                  <Input
                    id="logoUpload"
                    type="file"
                    accept="image/*"
                    className="mt-1"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 2 * 1024 * 1024) {
                          toast({
                            title: "File too large",
                            description: "Please select an image under 2MB.",
                            variant: "destructive",
                          });
                          return;
                        }
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const base64 = reader.result as string;
                          updateUserProfileMutation.mutate({ agencyLogo: base64 });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG or JPG, max 2MB. Recommended size: 200x80 pixels.
                  </p>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or use URL</span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="logoUrl">Logo URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="logoUrl"
                      placeholder="https://example.com/logo.png"
                      defaultValue={profile.agencyLogo?.startsWith("data:") ? "" : profile.agencyLogo || ""}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const value = (e.target as HTMLInputElement).value;
                          if (value) {
                            updateUserProfileMutation.mutate({ agencyLogo: value });
                          }
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        const input = document.getElementById("logoUrl") as HTMLInputElement;
                        if (input?.value) {
                          updateUserProfileMutation.mutate({ agencyLogo: input.value });
                        }
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <EmployeeDefaultSettingsCard
        ownerType="agency"
        heading="Employee Defaults"
        description="These defaults prefill every new agency employee."
      />
      </div>
    </div>
  );
}